import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { SelectItem } from '../select/select.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { isGuidString } from '../../../core/utils/guid.util';

export type AppComboboxVariant = 'select' | 'search-field';

/** `table-cell`: célula da lista; trigger neutro + pill interno quando há `colorHex` (cores fiéis ao cadastro). */
export type AppComboboxAppearance = 'default' | 'table-cell';

/** Texto para busca: minúsculas e sem acentos (ex.: "media" encontra "Média"). */
function normalizeForSearch(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

export interface ComboboxOptionGroup {
  heading: string;
  options: SelectItem[];
}

@Component({
  selector: 'app-combobox',
  standalone: true,
  imports: [UserAvatarComponent],
  host: {
    '[class.combo-host-table-cell]': 'appearance() === "table-cell" && variant() === "select"',
  },
  template: `
    <div
      class="combo"
      [class.combo--select]="variant() === 'select'"
      [class.combo--table-cell]="variant() === 'select' && appearance() === 'table-cell'"
    >
      @if (label()) {
        <span class="combo__label">{{ label() }}</span>
      }

      @if (variant() === 'select') {
        <div class="combo__control">
          <button
            type="button"
            class="combo__trigger"
            [class.combo__trigger--table-cell]="appearance() === 'table-cell'"
            [class.combo__trigger--table-cell-filled]="
              appearance() === 'table-cell' && !!selectedOption()?.colorHex
            "
            [style.background-color]="tableCellTriggerBg()"
            [style.color]="tableCellTriggerFg()"
            [disabled]="disabled()"
            [attr.aria-expanded]="open()"
            aria-haspopup="listbox"
            (click)="onTriggerClick($event)"
          >
            <span
              class="combo__trigger-text"
              [class.combo__trigger-text--placeholder]="!selectedOption() || !selectedOption()!.value"
              [class.combo__trigger-text--table-cell]="appearance() === 'table-cell'"
              [class.combo__trigger-text--person-cell]="
                personLayout() && appearance() === 'table-cell'
              "
            >
              @if (personLayout()) {
                <span class="combo__trigger-person" [class.combo__trigger-person--cell]="appearance() === 'table-cell'">
                  @if (selectedOption(); as so) {
                    @if (so.value) {
                      <span
                        class="combo__person-avatar-slot"
                        [style.--combo-avatar-px.px]="avatarSizePx()"
                        aria-hidden="true"
                      >
                        <app-user-avatar
                          [userId]="personUserId(so)"
                          [label]="so.label"
                          [size]="avatarSizePx()"
                        />
                      </span>
                      @if (appearance() !== 'table-cell') {
                        <span class="combo__trigger-person-name">{{ so.label }}</span>
                      }
                    } @else {
                      {{ closedPlaceholder() }}
                    }
                  } @else {
                    {{ closedPlaceholder() }}
                  }
                </span>
              } @else if (appearance() === 'table-cell') {
                @if (tableCellSelectedColorHex(); as cellHex) {
                  <span
                    class="combo__color-pill combo__color-pill--table-trigger"
                    [style.background-color]="cellHex"
                    [style.color]="getOptionTextColor(cellHex)"
                    >{{ selectedDisplayLabel() }}</span
                  >
                } @else {
                  {{ selectedDisplayLabel() }}
                }
              } @else if (!selectedOption()?.colorHex) {
                {{ selectedDisplayLabel() }}
              } @else {
                <span
                  class="combo__color-pill"
                  [style.background-color]="selectedOption()!.colorHex!"
                  [style.color]="getOptionTextColor(selectedOption()!.colorHex)"
                >
                  {{ selectedDisplayLabel() }}
                </span>
              }
            </span>
            <span
              class="combo__chevron"
              [class.combo__chevron--table-cell]="appearance() === 'table-cell'"
              aria-hidden="true"
            ></span>
          </button>

          @if (open() && !disabled() && (!fixedPanel() || fixedGeom())) {
            <div
              class="combo__dropdown"
              [class.combo__dropdown--person-wide]="personLayout() && !fixedGeom()"
              (mousedown)="onSelectPanelMouseDown($event)"
              [style.position]="fixedGeom() ? 'fixed' : null"
              [style.top]="fixedGeom() ? (fixedGeom()!.top === null ? 'auto' : fixedGeom()!.top + 'px') : null"
              [style.bottom.px]="fixedGeom()?.bottom ?? null"
              [style.max-height.px]="fixedGeom()?.maxHeight ?? null"
              [style.left.px]="fixedGeom()?.left ?? null"
              [style.width.px]="fixedGeom()?.width ?? null"
              [style.right]="fixedGeom() ? 'auto' : null"
              [style.z-index]="fixedGeom() ? '6000' : null"
            >
              <div class="combo__search-wrap combo__search-wrap--person">
                <input
                  type="text"
                  class="combo__panel-search"
                  [attr.placeholder]="searchPlaceholder()"
                  [value]="panelFilter()"
                  (input)="onPanelFilterInput($event)"
                  (click)="$event.stopPropagation()"
                />
              </div>
              <ul class="combo__list" role="listbox" [class.combo__list--person]="personLayout()">
                @if (useGroupedPersonPanel()) {
                  @for (grp of filteredOptionGroups(); track grp.heading) {
                    <li class="combo__group-head" role="presentation">{{ grp.heading }}</li>
                    @for (opt of grp.options; track opt.value) {
                      <li>
                        <button
                          type="button"
                          class="combo__opt"
                          [class.combo__opt--person]="personLayout()"
                          [class.combo__opt--person-empty]="personLayout() && isEmptyPersonOption(opt)"
                          [class.combo__opt--active]="opt.value === value()"
                          (click)="pick(opt.value)"
                        >
                          @if (personLayout()) {
                            @if (isEmptyPersonOption(opt)) {
                              <span class="combo__opt-title combo__opt-title--person-empty">{{ opt.label }}</span>
                            } @else {
                              <span
                                class="combo__person-avatar-slot"
                                [style.--combo-avatar-px.px]="avatarSizePx()"
                                aria-hidden="true"
                              >
                                <app-user-avatar
                                  [userId]="personUserId(opt)"
                                  [label]="opt.label"
                                  [size]="avatarSizePx()"
                                />
                              </span>
                              <span class="combo__opt-person-col">
                                <span class="combo__opt-title">{{ opt.label }}</span>
                              </span>
                            }
                          } @else if (opt.colorHex; as optionColor) {
                            <span
                              class="combo__opt-pill"
                              [style.background-color]="optionColor"
                              [style.color]="getOptionTextColor(optionColor)"
                            >
                              {{ opt.label }}
                            </span>
                          } @else {
                            <span class="combo__opt-title">{{ opt.label }}</span>
                            @if (opt.meta && opt.meta !== opt.label) {
                              <span class="combo__opt-meta">{{ opt.meta }}</span>
                            }
                          }
                        </button>
                      </li>
                    }
                  }
                  @if (!filteredOptionGroups().length) {
                    <li class="combo__empty">{{ emptyLabel() }}</li>
                  }
                } @else {
                  @for (opt of filteredOptions(); track opt.value) {
                    <li>
                      <button
                        type="button"
                        class="combo__opt"
                        [class.combo__opt--person]="personLayout()"
                        [class.combo__opt--person-empty]="personLayout() && isEmptyPersonOption(opt)"
                        [class.combo__opt--active]="opt.value === value()"
                        (click)="pick(opt.value)"
                      >
                        @if (personLayout()) {
                          @if (isEmptyPersonOption(opt)) {
                            <span class="combo__opt-title combo__opt-title--person-empty">{{ opt.label }}</span>
                          } @else {
                            <span
                              class="combo__person-avatar-slot"
                              [style.--combo-avatar-px.px]="avatarSizePx()"
                              aria-hidden="true"
                            >
                              <app-user-avatar
                                [userId]="personUserId(opt)"
                                [label]="opt.label"
                                [size]="avatarSizePx()"
                              />
                            </span>
                            <span class="combo__opt-person-col">
                              <span class="combo__opt-title">{{ opt.label }}</span>
                            </span>
                          }
                        } @else if (opt.colorHex; as optionColor) {
                          <span
                            class="combo__opt-pill"
                            [style.background-color]="optionColor"
                            [style.color]="getOptionTextColor(optionColor)"
                          >
                            {{ opt.label }}
                          </span>
                        } @else {
                          <span class="combo__opt-title">{{ opt.label }}</span>
                          @if (opt.meta && opt.meta !== opt.label) {
                            <span class="combo__opt-meta">{{ opt.meta }}</span>
                          }
                        }
                      </button>
                    </li>
                  }
                  @if (!filteredOptions().length) {
                    <li class="combo__empty">{{ emptyLabel() }}</li>
                  }
                }
              </ul>
            </div>
          }
        </div>
      } @else {
        <input
          type="text"
          class="combo__search"
          [disabled]="disabled()"
          [attr.placeholder]="searchPlaceholder()"
          [value]="searchText()"
          (input)="onSearch($event)"
          (focus)="!disabled() && open.set(true)"
          (blur)="onBlurSearchField()"
        />
        @if (open() && !disabled()) {
          <ul class="combo__list combo__list--floating" role="listbox">
            @for (opt of filteredOptions(); track opt.value) {
              <li>
                <button
                  type="button"
                  class="combo__opt"
                  [class.combo__opt--active]="opt.value === value()"
                  (mousedown)="$event.preventDefault()"
                  (click)="pick(opt.value)"
                >
                  <span class="combo__opt-title">{{ opt.label }}</span>
                  @if (opt.meta && opt.meta !== opt.label) {
                    <span class="combo__opt-meta">{{ opt.meta }}</span>
                  }
                </button>
              </li>
            }
            @if (!filteredOptions().length) {
              <li class="combo__empty">{{ emptyLabel() }}</li>
            }
          </ul>
        }
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }

      :host.combo-host-table-cell {
        display: flex;
        flex: 1;
        min-height: 0;
        min-width: 0;
        align-self: stretch;
      }

      :host.combo-host-table-cell .combo {
        flex: 1;
        min-height: 0;
        width: 100%;
      }

      .combo {
        position: relative;
        display: grid;
        gap: 6px;
        width: 100%;
        min-width: 0;
      }

      .combo__label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: var(--muted);
      }

      .combo__control {
        position: relative;
      }

      .combo__trigger {
        min-height: 36px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 0 10px 0 12px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        cursor: pointer;
        text-align: left;
        font: inherit;
      }

      .combo__trigger:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .combo__trigger:hover:not(:disabled) {
        border-color: color-mix(in srgb, var(--text) 12%, var(--border));
      }

      .combo__trigger:focus-visible {
        outline: 2px solid color-mix(in srgb, var(--active-text) 35%, transparent);
        outline-offset: 1px;
      }

      .combo__trigger-text {
        flex: 1;
        min-width: 0;
        font-size: 13px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .combo__color-pill {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Cor no pill interno — mesmo critério das opções do painel; trigger fica neutro e arredondado */
      /* Faixa colorida em toda a largura da célula (igual ao valor fechado na lista). */
      .combo__color-pill--table-trigger {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        padding: 5px 26px 5px 10px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        justify-content: center;
        min-height: 28px;
      }

      .combo__trigger-text--placeholder {
        color: var(--muted);
      }

      /* Avatar na célula: o texto não pode ser flex:1 com min-width:0 (esmagava o círculo). */
      .combo__trigger-text--person-cell {
        flex: 0 0 auto;
        min-width: auto;
        overflow: visible;
      }

      .combo__chevron {
        flex-shrink: 0;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 6px solid var(--muted);
        margin-top: 2px;
      }

      .combo__dropdown {
        position: absolute;
        z-index: 30;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        box-shadow: 0 8px 24px rgb(0 0 0 / 0.1);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        max-height: min(320px, 70vh);
      }

      /* Painel absoluto: não ficar só na largura da célula (personLayout + sem fixed). */
      .combo__dropdown--person-wide {
        left: auto;
        right: 0;
        width: max(100%, 288px);
        max-width: calc(100vw - 16px);
      }

      .combo__search-wrap {
        padding: 8px;
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
      }

      .combo__panel-search {
        width: 100%;
        min-height: 32px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
      }

      .combo__panel-search::placeholder {
        color: var(--muted);
      }

      .combo__search {
        min-height: 36px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
        width: 100%;
      }

      .combo__search:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .combo__list {
        margin: 0;
        padding: 4px;
        list-style: none;
        overflow: auto;
        flex: 1;
        min-height: 0;
        max-height: 240px;
      }

      .combo__list--floating {
        position: absolute;
        z-index: 20;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        max-height: 220px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        box-shadow: 0 8px 24px rgb(0 0 0 / 0.08);
      }

      .combo__opt {
        width: 100%;
        min-width: 0;
        text-align: left;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--text);
        font-size: 13px;
        padding: 8px 10px;
        cursor: pointer;
        transition: background 0.12s ease;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
      }

      .combo__opt-title {
        font-size: 13px;
        line-height: 1.35;
        font-weight: 500;
        word-break: break-word;
      }

      .combo__opt-pill {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.25;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .combo__opt-meta {
        font-size: 11px;
        line-height: 1.3;
        font-weight: 500;
        color: var(--muted);
        word-break: break-all;
      }

      .combo__opt:hover,
      .combo__opt--active {
        background: color-mix(in srgb, var(--active-bg) 12%, transparent);
      }

      .combo__empty {
        padding: 10px;
        font-size: 12px;
        color: var(--muted);
      }

      /* Lista / célula: trigger flexível; opções com pill arredondado */
      .combo--table-cell {
        height: 100%;
        min-height: 24px;
        gap: 0;
      }

      .combo--table-cell .combo__control {
        display: flex;
        align-items: stretch;
        height: 100%;
        min-height: 24px;
      }

      .combo--table-cell .combo__search-wrap {
        padding: 8px;
      }

      .combo--table-cell .combo__list:not(.combo__list--person) {
        padding: 4px;
      }

      .combo__trigger--table-cell {
        min-height: 28px;
        height: 100%;
        border-radius: 10px;
        padding: 2px 24px 2px 6px;
        justify-content: center;
        position: relative;
        /* Uma só linha entre colunas: o grip da lista já traz border-l; borda do trigger duplicava. */
        border: none;
        box-shadow: none;
      }

      .combo__trigger--table-cell:not(.combo__trigger--table-cell-filled) {
        background: var(--bg-surface);
      }

      .combo__trigger--table-cell:hover:not(:disabled) {
        background: color-mix(in srgb, var(--hover) 55%, transparent);
      }

      .combo__trigger--table-cell-filled {
        border: none;
        background: transparent;
      }

      .combo__trigger-text--table-cell {
        flex: 1;
        min-width: 0;
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .combo__trigger-text--table-cell.combo__trigger-text--placeholder {
        font-weight: 500;
      }

      .combo__chevron--table-cell {
        position: absolute;
        right: 8px;
        top: 50%;
        margin-top: 0;
        transform: translateY(-50%);
        border-top-color: currentColor;
        opacity: 0.72;
      }

      /* Opções do painel: mesma “faixa” full-width que a célula (evita mix pill gigante vs mini). */
      .combo--table-cell .combo__opt:not(.combo__opt--person) {
        flex-direction: row;
        align-items: stretch;
        justify-content: stretch;
        padding: 4px 6px;
        min-height: 0;
      }

      .combo--table-cell .combo__opt-pill {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        border-radius: 10px;
        justify-content: center;
        min-height: 30px;
        padding: 6px 10px;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0;
      }

      .combo__group-head {
        list-style: none;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
        padding: 10px 12px 4px;
        pointer-events: none;
      }

      .combo__opt--person {
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        gap: 10px;
        min-height: 40px;
      }

      .combo__opt-title--person-empty {
        padding-left: 38px;
        color: var(--muted);
        font-weight: 500;
      }

      .combo__person-avatar-slot {
        --combo-avatar-px: 28px;
        box-sizing: border-box;
        flex: 0 0 var(--combo-avatar-px);
        width: var(--combo-avatar-px);
        height: var(--combo-avatar-px);
        min-width: var(--combo-avatar-px);
        max-width: var(--combo-avatar-px);
        min-height: var(--combo-avatar-px);
        max-height: var(--combo-avatar-px);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        align-self: center;
        overflow: visible;
      }

      .combo__person-avatar-slot app-user-avatar {
        flex: none;
        flex-shrink: 0;
      }

      .combo__opt-person-col {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        min-width: 0;
        flex: 1;
      }

      .combo__opt--person .combo__opt-title {
        font-weight: 600;
      }

      .combo__opt--active.combo__opt--person {
        /* Só fundo como as outras linhas — sem “anel” que pareça borda no avatar. */
        box-shadow: none;
      }

      .combo__trigger-person {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        width: 100%;
        max-width: 100%;
      }

      .combo__trigger-person--cell {
        justify-content: center;
        gap: 0;
        width: auto;
        max-width: 100%;
        flex-shrink: 0;
      }

      .combo__trigger-person-name {
        flex: 1;
        min-width: 0;
        font-size: 13px;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .combo__list--person {
        padding: 6px 10px;
      }
    `,
  ],
})
export class AppComboboxComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private scrollCleanups: Array<() => void> = [];

  readonly label = input<string>('');
  readonly options = input<SelectItem[]>([]);
  readonly value = input<string>('');
  readonly valueChange = output<string | null>();
  readonly searchPlaceholder = input<string>('');
  readonly emptyLabel = input<string>('');
  readonly closedPlaceholder = input<string>('');
  readonly variant = input<AppComboboxVariant>('select');
  readonly disabled = input(false);
  /** Painel em `position: fixed` (evita corte por `overflow` de ancestrais, ex.: modal). */
  readonly fixedPanel = input(false);
  /** Visual de célula na tabela (lista): preenchimento forte, raio mínimo. */
  readonly appearance = input<AppComboboxAppearance>('default');
  /** Linhas com avatar + nome (responsável / pessoa). */
  readonly personLayout = input(false);
  /** Secções no painel (ex.: Responsáveis / Pessoas). Só com `personLayout`. */
  readonly optionGroups = input<ComboboxOptionGroup[] | null>(null);
  /** Diâmetro do avatar (círculo fixo). */
  readonly avatarSizePx = input(28);
  /**
   * Largura mínima do painel com `fixedPanel` (px). `null` = automático: com `personLayout` usa 288px
   * para o dropdown não ficar preso à coluna estreita da tabela.
   */
  readonly panelMinWidthPx = input<number | null>(null);

  readonly open = signal(false);
  readonly searchText = signal('');
  readonly panelFilter = signal('');
  readonly fixedGeom = signal<{ top: number | null; bottom: number | null; left: number; width: number; maxHeight: number } | null>(null);

  constructor() {
    effect(() => {
      if (this.variant() !== 'search-field') return;
      const v = this.value();
      const opt = this.allSelectOptions().find((o) => o.value === v);
      if (opt) this.searchText.set(opt.label);
    });

    effect((onCleanup) => {
      if (this.variant() !== 'select' || !this.fixedPanel()) {
        this.fixedGeom.set(null);
        this.unbindFixedPanelScrollSync();
        return;
      }
      if (!this.open()) {
        this.fixedGeom.set(null);
        this.unbindFixedPanelScrollSync();
        return;
      }
      queueMicrotask(() => {
        if (!this.open() || !this.fixedPanel() || this.variant() !== 'select') return;
        this.syncFixedGeom();
        this.bindFixedPanelScrollSync();
      });
      onCleanup(() => {
        this.unbindFixedPanelScrollSync();
        this.fixedGeom.set(null);
      });
    });
  }

  readonly allSelectOptions = computed(() => {
    const gr = this.optionGroups();
    if (gr?.length) {
      const out: SelectItem[] = [];
      const seen = new Set<string>();
      for (const x of gr) {
        for (const o of x.options) {
          if (seen.has(o.value)) continue;
          seen.add(o.value);
          out.push(o);
        }
      }
      return out;
    }
    return this.options();
  });

  readonly useGroupedPersonPanel = computed(
    () => this.personLayout() && (this.optionGroups()?.length ?? 0) > 0
  );

  readonly selectedOption = computed(() => {
    const v = this.value();
    return this.allSelectOptions().find((o) => o.value === v) ?? null;
  });

  readonly selectedDisplayLabel = computed(() => {
    const opt = this.selectedOption();
    if (opt) {
      if (this.personLayout()) {
        return opt.label;
      }
      if (opt.meta && opt.meta !== opt.label) {
        return `${opt.label} · ${opt.meta}`;
      }
      return opt.label;
    }
    return this.closedPlaceholder();
  });

  readonly filteredOptionGroups = computed((): ComboboxOptionGroup[] => {
    const gr = this.optionGroups();
    if (!gr?.length) return [];
    const q = normalizeForSearch(this.panelFilter().trim());
    const match = (o: SelectItem): boolean => {
      if (!q) return true;
      const meta = normalizeForSearch(o.meta ?? '');
      const label = normalizeForSearch(o.label);
      const val = normalizeForSearch(o.value);
      return label.includes(q) || meta.includes(q) || val.includes(q);
    };
    return gr.map((g) => ({ heading: g.heading, options: g.options.filter(match) })).filter((g) => g.options.length > 0);
  });

  readonly filteredOptions = computed(() => {
    const opts = this.allSelectOptions();
    const current = this.value();
    const selectedOpt = opts.find((o) => o.value === current);

    if (this.variant() === 'search-field') {
      const raw = this.searchText().trim();
      const q = normalizeForSearch(raw);
      if (selectedOpt && raw === selectedOpt.label.trim()) {
        return opts;
      }
      if (!q) return opts;
      return opts.filter((o) => {
        const meta = normalizeForSearch(o.meta ?? '');
        const label = normalizeForSearch(o.label);
        const val = normalizeForSearch(o.value);
        return label.includes(q) || meta.includes(q) || val.includes(q);
      });
    }

    const q = normalizeForSearch(this.panelFilter().trim());
    if (!q) return opts;
    return opts.filter((o) => {
      const meta = normalizeForSearch(o.meta ?? '');
      const label = normalizeForSearch(o.label);
      const val = normalizeForSearch(o.value);
      return label.includes(q) || meta.includes(q) || val.includes(q);
    });
  });

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(ev: PointerEvent): void {
    if (!this.open()) return;
    const root = this.host.nativeElement;
    if (!root.contains(ev.target as Node)) {
      this.open.set(false);
      if (this.variant() === 'select') {
        this.panelFilter.set('');
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    if (ev.key !== 'Escape' || !this.open()) return;
    ev.stopPropagation();
    this.open.set(false);
    if (this.variant() === 'select') {
      this.panelFilter.set('');
    }
  }

  onTriggerClick(ev: Event): void {
    if (this.disabled()) return;
    ev.stopPropagation();
    const next = !this.open();
    this.open.set(next);
    if (next) {
      this.panelFilter.set('');
      queueMicrotask(() => {
        const el = this.host.nativeElement.querySelector('.combo__panel-search');
        if (el instanceof HTMLInputElement) el.focus();
      });
    }
  }

  onPanelFilterInput(ev: Event): void {
    const t = (ev.target as HTMLInputElement).value;
    this.panelFilter.set(t);
  }

  /**
   * Evita que o trigger perca foco ao clicar na lista, mas não bloqueia o input de pesquisa
   * (`preventDefault` no painel inteiro impedia digitar/filtrar em alguns casos).
   */
  onSelectPanelMouseDown(ev: MouseEvent): void {
    const t = ev.target as HTMLElement | null;
    if (t?.closest('.combo__search-wrap')) {
      return;
    }
    ev.preventDefault();
  }

  pick(v: string): void {
    const next = v.length ? v : '';
    if (next === this.value()) {
      this.open.set(false);
      this.panelFilter.set('');
      return;
    }
    this.valueChange.emit(v.length ? v : null);
    if (this.variant() === 'search-field') {
      const picked = this.allSelectOptions().find((o) => o.value === v);
      this.searchText.set(picked?.label ?? '');
    }
    this.open.set(false);
    this.panelFilter.set('');
  }

  onSearch(ev: Event): void {
    const t = (ev.target as HTMLInputElement).value;
    this.searchText.set(t);
    this.open.set(true);
  }

  onBlurSearchField(): void {
    setTimeout(() => this.open.set(false), 120);
    const current = this.value();
    const match = this.allSelectOptions().find((o) => o.value === current);
    this.searchText.set(match?.label ?? this.searchText());
  }

  /** Hex da opção selecionada na célula da lista (para pill interno, fiel ao cadastro). */
  tableCellSelectedColorHex(): string | null {
    if (this.appearance() !== 'table-cell') return null;
    const hex = this.selectedOption()?.colorHex?.trim();
    return hex && hex.length > 0 ? hex : null;
  }

  tableCellTriggerBg(): string | null {
    if (this.appearance() !== 'table-cell') return null;
    if (this.tableCellSelectedColorHex()) return 'transparent';
    return null;
  }

  tableCellTriggerFg(): string | null {
    if (this.appearance() !== 'table-cell') return null;
    if (this.tableCellSelectedColorHex()) return 'var(--muted)';
    return null;
  }

  getOptionTextColor(hexColor: string | null | undefined): string {
    if (!hexColor) return 'var(--text)';
    const normalized = hexColor.trim().toUpperCase();
    const match = /^#([0-9A-F]{6})$/.exec(normalized);
    if (!match) return 'var(--text)';
    const rgb = match[1];
    const r = Number.parseInt(rgb.slice(0, 2), 16);
    const g = Number.parseInt(rgb.slice(2, 4), 16);
    const b = Number.parseInt(rgb.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? '#111827' : '#FFFFFF';
  }

  private syncFixedGeom(): void {
    if (!this.fixedPanel() || !this.open() || this.variant() !== 'select') return;
    const btn = this.host.nativeElement.querySelector('.combo__trigger');
    if (!(btn instanceof HTMLElement)) return;
    const r = btn.getBoundingClientRect();
    const triggerW = Math.round(r.width);
    const explicitMin = this.panelMinWidthPx();
    const defaultPersonMin = this.personLayout() ? 288 : 0;
    const minW = explicitMin ?? defaultPersonMin;
    let width = minW > 0 ? Math.max(triggerW, minW) : triggerW;
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const margin = 8;
    const gap = 4;
    const leftEdge = viewportLeft + margin;
    const rightEdge = viewportLeft + viewportWidth - margin;
    const topEdge = viewportTop + margin;
    const bottomEdge = viewportTop + viewportHeight - margin;
    width = Math.min(width, Math.max(0, rightEdge - leftEdge));
    const left = Math.max(leftEdge, Math.min(Math.round(r.left), rightEdge - width));
    const below = Math.max(0, bottomEdge - r.bottom - gap);
    const above = Math.max(0, r.top - gap - topEdge);
    const preferredHeight = Math.min(320, viewportHeight * 0.7);
    const openAbove = below < preferredHeight && above > below;
    this.fixedGeom.set({
      // Anchor the upper menu by its bottom so short/filtered lists stay next to the trigger.
      top: openAbove ? null : Math.max(topEdge, Math.min(r.bottom + gap, bottomEdge)),
      bottom: openAbove ? window.innerHeight - Math.max(topEdge, Math.min(r.top - gap, bottomEdge)) : null,
      left,
      width,
      maxHeight: Math.min(preferredHeight, openAbove ? above : below),
    });
  }

  private bindFixedPanelScrollSync(): void {
    this.unbindFixedPanelScrollSync();
    const sync = () => this.syncFixedGeom();
    const onResize = () => sync();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', sync, { passive: true });
    this.scrollCleanups.push(() => window.removeEventListener('scroll', sync));
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', sync);
    viewport?.addEventListener('scroll', sync);
    this.scrollCleanups.push(() => {
      viewport?.removeEventListener('resize', sync);
      viewport?.removeEventListener('scroll', sync);
    });
    this.scrollCleanups.push(() => window.removeEventListener('resize', onResize));

    let el: HTMLElement | null = this.host.nativeElement;
    while (el) {
      const st = getComputedStyle(el);
      const scrollY = /(auto|scroll|overlay)/.test(st.overflowY) && el.scrollHeight > el.clientHeight;
      const scrollX = /(auto|scroll|overlay)/.test(st.overflowX) && el.scrollWidth > el.clientWidth;
      if (scrollY || scrollX) {
        el.addEventListener('scroll', sync, { passive: true });
        const target = el;
        this.scrollCleanups.push(() => target.removeEventListener('scroll', sync));
      }
      el = el.parentElement;
    }
  }

  private unbindFixedPanelScrollSync(): void {
    for (const c of this.scrollCleanups) c();
    this.scrollCleanups = [];
  }

  /** Sem valor: não mostrar avatar no painel (só o label, ex. “—”). */
  isEmptyPersonOption(opt: SelectItem): boolean {
    return !(opt.value ?? '').toString().trim();
  }

  personUserId(opt: SelectItem): string | null {
    const u = (opt.userId ?? '').trim();
    if (u) return u;
    const v = (opt.value ?? '').trim();
    return isGuidString(v) ? v : null;
  }
}
